/*eslint-disable*/
import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../../reducer/loginSlice';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Receipt,
  Package,
  FileText,
  Plane,
  MapPin,
  Calculator,
  Truck,
  Building2,
  ClipboardList,
  UserCog,
  FileSpreadsheet,
  Wallet,
  DollarSign,
  Settings,
  LogOut,
  Menu,
  ArrowLeft,
  ShieldAlert,
  CheckCircle,
  Upload,
  FileCheck,
  BadgeCheck,
  RefreshCw,
  AlertTriangle,
  CreditCardIcon,
  BarChart2,
  ChevronDown,
  ChevronRight,
  Search,
  Timer,
  ListOrdered,
  Plug,
  BookOpen,
  Layers,
  DownloadCloud,
} from "lucide-react";
import { SvgElement, icontypesEnum } from "../assets/svgElement";
import { AuthApis } from "../../apis/authApis";
import { AxiosResponse } from "axios";

// Menu configuration, grouped into collapsible sections.
const getMenuSections = (permissions: any, userRole: any, fleetId: any) => {
  const sections = [
    {
      title: 'Overview',
      icon: LayoutDashboard,
      items: [
        { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: '/admin/admin-dashboard', permission: permissions.canViewAdminStat },
        { id: 'analytics', label: 'Analytics', icon: BarChart2, path: '/analytics', permission: userRole !== 'rider' && userRole !== 'driver' },
        { id: 'tracking', label: 'Tracking', icon: MapPin, path: '/admin/tracking', permission: true },
        { id: 'calculate-rate', label: 'Calculate Rate', icon: Calculator, path: '/admin/calculate-rate', permission: true },
      ],
    },
    {
      title: 'Shipments & Operations',
      icon: Package,
      items: [
        { id: 'shipments', label: 'Shipments', icon: Package, path: '/admin/shipments', permission: permissions.canManageShipment || userRole === 'rider' },
        { id: 'manifest', label: 'Manifest', icon: FileText, path: '/admin/manifest', permission: permissions.canManageManifest || userRole === 'fleet' },
        { id: 'runway-manifest', label: 'Runway Manifest', icon: Plane, path: '/admin/runway-manifest', permission: permissions.canManageRunwayManifest || userRole === 'rider' || userRole === 'driver' || userRole === 'fleet' },
        { id: 'ffdx-feed', label: 'FFDX Feed', icon: DownloadCloud, path: '/admin/ffdx-feed', permission: permissions.canManageFfdxFeed },
        { id: 'rider-applications', label: 'Rider Applications', icon: UserCog, path: '/admin/rider-applications', permission: permissions.canManageRidersApplication },
      ],
    },
    {
      title: 'Fleet & Riders',
      icon: Truck,
      items: [
        { id: 'manage-fleet', label: 'Manage Fleet', icon: Truck, path: '/admin/manage-riders', permission: permissions.canManageFleet || userRole === 'fleet' },
        { id: 'fleet-riders', label: 'My Riders', icon: Users, path: '/fleet/my-riders', permission: userRole === 'fleet' },
        { id: 'rider-wallet', label: 'My Wallet', icon: Wallet, path: '/rider/wallet', permission: (userRole === 'rider' && !fleetId) || userRole === 'fleet' || (userRole === 'driver' && !fleetId) },
      ],
    },
    {
      title: 'Wallets & Loans',
      icon: Wallet,
      items: [
        { id: 'admin-wallets', label: 'Rider/Fleet Wallets', icon: Wallet, path: '/admin/wallets', permission: permissions.canViewWallets },
        { id: 'riders-witdrawal', label: 'Riders Withdrawal', icon: CreditCardIcon, path: '/admin/withdrawals', permission: permissions.canViewRidersWithdrawal },
        { id: 'rider-loans', label: 'Rider Loans', icon: DollarSign, path: '/admin/rider-loans', permission: permissions.canManageRiderLoans },
        { id: 'loan-wallet', label: 'Loan Wallet', icon: Wallet, path: '/admin/loan-wallet', permission: permissions.canManageRiderLoans },
      ],
    },
    {
      title: 'Payments',
      icon: DollarSign,
      items: [
        { id: 'payment-confirmation', label: 'Payment Confirmation', icon: CheckCircle, path: '/admin/payment-confirmation', permission: permissions.canConfirmPayment },
        { id: 'record-bank-transfer', label: 'Record Bank Transfer', icon: Upload, path: '/admin/payment-proofs/submit', permission: permissions.canSubmitPaymentProof },
        { id: 'payment-proofs', label: 'Payment Proofs', icon: FileCheck, path: '/admin/payment-proofs', permission: permissions.canConfirmPaymentProof },
        { id: 'payment-approval', label: 'Payment Approval', icon: BadgeCheck, path: '/admin/payment-approval', permission: permissions.canApprovePayment },
        { id: 'delivery-payments', label: 'Delivery Payments', icon: DollarSign, path: '/admin/delivery-payments', permission: permissions.canViewApprovedPayment },
        { id: 'custom-duty', label: 'Custom Duty', icon: FileSpreadsheet, path: '/admin/custom-duty', permission: permissions.canManageCustomDuty },
        { id: 'notification-channels', label: 'Notifications', icon: FileSpreadsheet, path: '/admin/notification-channels', permission: permissions.canManageNotificationChannels },
        { id: 'reimbursements', label: 'Reimbursements', icon: DollarSign, path: '/admin/reimbursements', permission: permissions.canInitiateReimbursement || permissions.canApproveReimbursementL1 || permissions.canApproveReimbursementL2 },
      ],
    },
    {
      title: 'Finance',
      icon: FileSpreadsheet,
      items: [
        { id: 'credit-customers', label: 'Credit Customer', icon: CreditCard, path: '/admin/credit-customers', permission: permissions.canManageCreditCustomers },
        { id: 'manage-invoice', label: 'Manage Invoice', icon: FileSpreadsheet, path: '/admin/manage-invoice', permission: permissions.canManageInvoice },
        { id: 'manage-expense', label: 'Manage Expenses', icon: Wallet, path: '/admin/manage-expense', permission: permissions.canManageInvoice },
        // Mirrors the route guard in App.tsx (`can_create_expense_voucher`).
        // Was a hand-rolled role check that evaluated true for every role, so
        // the link showed to everyone and the route then bounced them out.
        { id: 'expense-voucher', label: 'Expense Voucher', icon: Receipt, path: '/admin/expense-voucher', permission: permissions.canCreateExpenseVoucher },
        { id: 'manage-rates', label: 'Manage Rates', icon: DollarSign, path: '/admin/manage-rates', permission: permissions.canManageRates },
        // Corporate client billing (SHEIN et al.) — its own zones and rates,
        // deliberately independent of Manage Rates above.
        { id: 'billing-zones', label: 'Billing Zones & Rates', icon: Layers, path: '/admin/billing/zones', permission: permissions.canManageBilling },
        { id: 'client-invoices', label: 'Client Invoices', icon: Receipt, path: '/admin/billing/invoices', permission: permissions.canManageBilling },
      ],
    },
    {
      title: 'Risk & Compliance',
      icon: ShieldAlert,
      items: [
        { id: 'fine-settings', label: 'Fine Settings', icon: AlertTriangle, path: '/admin/fine-settings', permission: permissions.canManageFineSettings },
        { id: 'pending-fines', label: 'Pending Fines', icon: AlertTriangle, path: '/admin/pending-fines', permission: permissions.canViewPendingFines },
        { id: 'auto-attempted', label: 'Auto-Attempted', icon: Timer, path: '/admin/auto-attempted-deliveries', permission: permissions.canViewAutoAttempted },
        { id: 'fraud-dashboard', label: 'Fraud Monitor', icon: ShieldAlert, path: '/admin/fraud-dashboard', permission: permissions.canViewFraudMonitor },
        { id: 'audits', label: 'Audits', icon: ClipboardList, path: '/admin/audits', permission: permissions.canViewAudit },
      ],
    },
    {
      title: 'Administration',
      icon: UserCog,
      items: [
        { id: 'users', label: 'Users', icon: Users, path: '/admin/users', permission: permissions.canViewUsers },
        { id: 'manage-staffs', label: 'Manage Staffs', icon: UserCog, path: '/admin/manage-staffs', permission: permissions.canManageStaffs },
        { id: 'manage-branch', label: 'Manage Branch', icon: Building2, path: '/admin/manage-branch', permission: permissions.canManageBranch },
        { id: 'voucher-settings', label: 'Voucher Settings', icon: ListOrdered, path: '/admin/voucher-settings', permission: permissions.canManageVoucherSettings },
        // Partner / customer API gateway — from the api-integration branch,
        // which gated it on the admin-stat permission.
        { id: 'api-gateway', label: 'API Gateway', icon: Plug, path: '/admin/api-gateway', permission: permissions.canViewAdminStat },
        // Same gate as the API Gateway itself — whoever administers the gateway can
        // read its manual, nobody else. It documents internal admin endpoints.
        { id: 'api-documentation', label: 'API Documentation', icon: BookOpen, path: '/admin/api-documentation', permission: permissions.canViewAdminStat },
        { id: 'legacy-migration', label: 'Legacy Migration', icon: RefreshCw, path: '/admin/legacy-migration', permission: permissions.canManageLegacyMigration },
      ],
    },
  ];

  // Keep only permitted items, then drop any section left empty.
  return sections
    .map(section => ({ ...section, items: section.items.filter(item => item.permission) }))
    .filter(section => section.items.length > 0);
};

// Open the section that contains the active route; collapse the rest.
const computeOpenSections = (sections: any[], pathname: string) => {
  const active = sections.find(s => s.items.some((i: any) => pathname.startsWith(i.path)));
  const map: Record<string, boolean> = {};
  sections.forEach(s => { map[s.title] = active ? s.title === active.title : true; });
  return map;
};

export default function AdminSidebar({ title }: { title: string }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [collapseShow, setCollapseShow] = React.useState("hidden");
  const userLoginData = useSelector((state: any) => state.data.login.value);

  // Process permissions
  const permissions = React.useMemo(() => {
    const userPermissions = userLoginData?.data?.permissions
      ? JSON.parse(userLoginData.data.permissions)
      : [];

    const isAdmin = userLoginData?.data?.role === 'admin';

    return {
      canViewAdminStat: isAdmin || userPermissions.includes('can_view_admin_stat'),
      canViewUsers: isAdmin || userPermissions.includes('can_view_users'),
      canManageCreditCustomers: isAdmin || userPermissions.includes('can_manage_credit_customers'),
      canCreateExpenseVoucher: isAdmin || userPermissions.includes('can_create_expense_voucher'),
      canManageVoucherSettings: isAdmin || userPermissions.includes('can_manage_voucher_settings'),
      canManageShipment: isAdmin || userPermissions.includes('can_manage_shipment'),
      canManageManifest: isAdmin || userPermissions.includes('can_manage_manifest'),
      canManageRunwayManifest: isAdmin || userPermissions.includes('can_manage_runway_manifest'),
      canManageFleet: isAdmin || userPermissions.includes('can_manage_fleet'),
      canManageBranch: isAdmin || userPermissions.includes('can_manage_branch'),
      canViewAudit: isAdmin || userPermissions.includes('can_view_audit'),
      canManageStaffs: isAdmin || userPermissions.includes('can_manage_staffs'),
      canManageInvoice: isAdmin || userPermissions.includes('can_manage_invoice'),
      canManageRates: isAdmin || userPermissions.includes('can_manage_rates'),
      canManageRidersApplication: isAdmin || userPermissions.includes('can_manage_riders_application'),
      canConfirmPayment: isAdmin || userPermissions.includes('can_confirm_payment'),
      canSubmitPaymentProof: isAdmin || userPermissions.includes('can_submit_payment_proof'),
      canConfirmPaymentProof: isAdmin || userPermissions.includes('can_confirm_payment_proof'),
      canApprovePayment: isAdmin || userPermissions.includes('can_approve_payment'),
      canViewApprovedPayment: isAdmin || userPermissions.includes('can_view_approved_payment'),
      canManageLegacyMigration: isAdmin || userPermissions.includes('can_manage_legacy_migration'),
      canViewRidersWithdrawal: isAdmin || userPermissions.includes('can_view_riders_witdrawal'),
      canViewWallets: isAdmin || userPermissions.includes('can_view_wallets'),
      canManageWallets: isAdmin || userPermissions.includes('can_manage_wallets'),
      canManageFineSettings: isAdmin || userPermissions.includes('can_manage_fine_settings'),
      canViewPendingFines: isAdmin || userPermissions.includes('can_view_pending_fines'),  // NEW permission
      canViewAutoAttempted: isAdmin || userPermissions.includes('can_view_auto_attempted'),
      canViewFraudMonitor: isAdmin || userPermissions.includes('can_view_fraud_monitor'),
      canManageRiderLoans: isAdmin || userPermissions.includes('can_manage_rider_loans'),
      canManageCustomDuty: isAdmin || userPermissions.includes('can_manage_custom_duty'),
      canManageNotificationChannels: isAdmin || userPermissions.includes('can_manage_notification_channels'),
      canManageBilling: isAdmin || userPermissions.includes('can_manage_billing'),
      canManageFfdxFeed: isAdmin || userPermissions.includes('can_manage_ffdx_feed'),
      canInitiateReimbursement: isAdmin || userPermissions.includes('can_initiate_reimbursement'),
      canApproveReimbursementL1: isAdmin || userPermissions.includes('can_approve_reimbursement_l1'),
      canApproveReimbursementL2: isAdmin || userPermissions.includes('can_approve_reimbursement_l2'),
      canViewAllStateAnalytics: isAdmin || userPermissions.includes('can_view_all_state_analytics'),
    };
  }, [userLoginData]);

  const location = useLocation();

  const sections = React.useMemo(
    () => getMenuSections(permissions, userLoginData?.data?.role, userLoginData?.data?.fleet_id),
    [permissions, userLoginData?.data?.role, userLoginData?.data?.fleet_id]
  );

  const [openSections, setOpenSections] = React.useState<Record<string, boolean>>(
    () => computeOpenSections(sections, location.pathname)
  );

  // Re-focus the active section whenever the route changes.
  React.useEffect(() => {
    setOpenSections(computeOpenSections(sections, location.pathname));
  }, [location.pathname, sections.length]);

  const toggleSection = (title: string) =>
    setOpenSections(prev => ({ ...prev, [title]: !(prev[title] ?? true) }));

  // The section containing the current route (for highlighting).
  const activeSectionTitle = React.useMemo(() => {
    const active = sections.find(s => s.items.some((i: any) => location.pathname.startsWith(i.path)));
    return active?.title;
  }, [sections, location.pathname]);

  // Search / filter
  const [query, setQuery] = React.useState('');
  const q = query.trim().toLowerCase();
  const visibleSections = React.useMemo(() => {
    if (!q) return sections;
    return sections
      .map(s => ({ ...s, items: s.items.filter((i: any) => i.label.toLowerCase().includes(q)) }))
      .filter(s => s.items.length > 0);
  }, [sections, q]);

  // Session check
  React.useEffect(() => {
    AuthApis.session()
      .then((response) => {
        if (!response?.data?.status) {
          dispatch(login([]));
          navigate('/staff-login');
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  // Verification check
  React.useEffect(() => {
    if (userLoginData?.data?.isVerified === 'false') {
      verify();
    }
  }, []);

  // Role check
  React.useEffect(() => {
    if (userLoginData?.data?.role === 'user') {
      dispatch(login([]));
      navigate('/staff-login');
    }
  }, []);

  const logOut = () => {
    AuthApis.logout('')
      .then((response) => {
        if (response?.data) {
          dispatch(login([]));
          navigate('/staff-login');
        }
      })
      .catch((error) => {
        console.log(error.response?.data);
      });
  };

  const verify = () => {
    AuthApis.logout('')
      .then((response) => {
        dispatch(login([]));
        navigate('/email-verify');
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <nav className="md:left-0 md:block md:fixed md:top-0 md:bottom-0 md:flex-row md:flex-nowrap md:overflow-hidden shadow-xl md:bg-[#ffffff] mt-2 ml-1 relative md:w-64 z-2 py-2 px-1">
      <div className="md:flex-col md:items-stretch md:min-h-full md:flex-nowrap px-0 flex flex-wrap items-center justify-between w-full mx-auto">
        {/* Mobile Toggler */}
        <button
          className="cursor-pointer text-black md:hidden px-3 py-1 text-xl leading-none bg-transparent rounded border border-solid border-transparent"
          type="button"
          onClick={() => setCollapseShow("bg-white m-2 py-3 px-6")}
        >
          <Menu className="inline-block mr-2" size={24} />
          <span className="text-black">{title}</span>
        </button>

        {/* Mobile Header */}
        <span className="flex justify-end md:hidden">
          <span className="flex flex-row flex-wrap items-center lg:ml-auto mr-3">
            <div className="relative flex w-full flex-wrap items-stretch gap-5">
              <h2 className="pt-2 capitalize">
                {userLoginData?.data?.role?.replace(/_/g, ' ')}
              </h2>
              <NavLink to="/editprofile">
                <Settings className="mt-2" size={20} />
              </NavLink>
            </div>
          </span>
        </span>

        {/* Brand Logo */}
        <Link className="hidden md:pb-6 mt-10 lg:flex justify-center" to="/">
          <SvgElement type={icontypesEnum.BARS2} />
        </Link>

        {/* Collapsible Menu */}
        <div
          className={
            "md:flex md:flex-col items-stretch md:opacity-100 md:relative md:mt-4 md:shadow-none shadow bg-[#ffffff] absolute top-4 left-0 right-1 h-auto flex-1 rounded " +
            collapseShow
          }
        >
          {/* Mobile Collapse Header */}
          <div className="md:min-w-full md:hidden block pb-1 mb-4">
            <div className="flex justify-between">
              <span
                className="flex justify-start gap-1 cursor-pointer"
                onClick={() => setCollapseShow("hidden")}
              >
                <ArrowLeft className="bg-gray-200 text-gray-500 rounded-full p-1 h-6 w-6" />
                <span className="text-white ml-2">Back</span>
              </span>

              <NavLink to="/editprofile" onClick={() => setCollapseShow("hidden")}>
                <Settings className="text-white" size={24} />
              </NavLink>
            </div>
          </div>

          {/* Search / filter */}
          <div className="px-3 mb-2 md:mt-1 mt-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B0B0B0]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search menu..."
                className="w-full bg-[#F5F5F5] border border-transparent focus:border-[#E04343] focus:bg-white rounded-[8px] pl-9 pr-3 py-2 text-[13px] text-[#4A4A4A] outline-none transition-colors"
              />
            </div>
          </div>

          {/* Menu Items — grouped into collapsible sections */}
          <ul className="flex-col list-none flex bg-[#ffffff] overflow-auto max-h-[72vh] pb-2">
            {visibleSections.length === 0 && (
              <li className="px-4 py-6 text-center text-[13px] text-gray-400">No results</li>
            )}
            {visibleSections.map((section) => {
              const isOpen = q ? true : (openSections[section.title] ?? true);
              const isActiveSection = section.title === activeSectionTitle;
              const SectionIcon = (section as any).icon;
              return (
                <li key={section.title} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => !q && toggleSection(section.title)}
                    className={`w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide transition-colors ${isActiveSection ? 'text-[#E04343]' : 'text-[#9AA0A6] hover:text-[#6B7075]'}`}
                  >
                    {SectionIcon && <SectionIcon size={15} className="shrink-0" />}
                    <span className="flex-1 text-left">{section.title}</span>
                    {!q && (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                  </button>

                  {isOpen && (
                    <ul className="list-none mb-1">
                      {section.items.map((item: any) => {
                        const IconComponent = item.icon;
                        return (
                          <li key={item.id} className="items-center">
                            <NavLink
                              to={item.path}
                              onClick={() => setCollapseShow("hidden")}
                              className="flex items-center mx-3 my-0.5 py-[8px] pl-4 gap-3 rounded-[6px] text-[13.5px] text-[#6B7075] border-l-[3px] border-transparent hover:bg-[#FF3B300D] hover:text-[#E04343] hover:border-[#E04343] transition-colors"
                              style={({ isActive }) =>
                                isActive
                                  ? { color: '#ffffff', background: '#E04343', borderLeftColor: '#B91C1C' }
                                  : {}
                              }
                            >
                              <IconComponent size={18} className="shrink-0" />
                              <span>{item.label}</span>
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}

            {/* Logout */}
            <li className="items-center mx-3 pl-3 pt-6">
              <span
                className="text-xs cursor-pointer block"
                onClick={logOut}
              >
                <span className="flex py-2 cursor-pointer">
                  <LogOut className="mr-3" size={24} color="red" />
                  <span className="text-[15px] mt-1 font-normal text-[#FF0000]">
                    Sign out
                  </span>
                </span>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}